import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { EmptyState, StatusBadge } from '@pazportal/ui';
import { formatCurrency, formatDate } from '../lib/formatters';

type InvoiceRow = {
  id: string;
  customer_id: string;
  number: string | null;
  status: string;
  currency: string;
  amount_due_cents: number;
  amount_paid_cents: number;
  amount_remaining_cents: number;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  hosted_invoice_url: string | null;
  customer?: { id: string; name: string } | null;
};

type Status = 'all' | 'open' | 'paid' | 'uncollectible' | 'void';

function useInvoices() {
  return useQuery({
    queryKey: ['admin-invoices'],
    queryFn: async () => {
      // invoices not yet in generated types — cast through unknown.
      const client = supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => {
            order: (c: string, o: { ascending: boolean }) => {
              limit: (n: number) => Promise<{ data: InvoiceRow[] | null; error: { message: string } | null }>;
            };
          };
        };
      };
      const { data, error } = await client
        .from('invoices')
        .select('id, customer_id, number, status, currency, amount_due_cents, amount_paid_cents, amount_remaining_cents, issued_at, due_at, paid_at, hosted_invoice_url, customer:customers(id, name)')
        .order('issued_at', { ascending: false })
        .limit(200);
      if (error) {
        // Table may not exist yet in a fresh project — treat as empty.
        if (error.message?.includes('does not exist')) return [];
        throw new Error('invoices unavailable');
      }
      return data ?? [];
    },
    staleTime: 60_000,
  });
}

export function InvoicesPage() {
  const navigate = useNavigate();
  const { data: invoices, isLoading } = useInvoices();
  const [filter, setFilter] = useState<Status>('all');

  const filtered = useMemo(() => {
    const list = invoices ?? [];
    return filter === 'all' ? list : list.filter((i) => i.status === filter);
  }, [invoices, filter]);

  const totals = useMemo(() => {
    const list = invoices ?? [];
    return {
      open_cents: list.filter((i) => i.status === 'open').reduce((s, i) => s + i.amount_remaining_cents, 0),
      paid_30d_cents: list
        .filter((i) => i.status === 'paid' && i.paid_at && new Date(i.paid_at) > new Date(Date.now() - 30 * 86400_000))
        .reduce((s, i) => s + i.amount_paid_cents, 0),
      uncollectible_cents: list.filter((i) => i.status === 'uncollectible').reduce((s, i) => s + i.amount_remaining_cents, 0),
    };
  }, [invoices]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-text">Invoices</h1>
          <p className="text-sm text-text-muted mt-0.5">
            Billing is managed in Zoho Billing. The invoice sync from Zoho is scoped in docs/zoho-billing-invoices-scope.md; until it ships this ledger is empty.
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          {(['all', 'open', 'paid', 'uncollectible', 'void'] as const).map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                filter === k
                  ? 'bg-primary text-text-inverse'
                  : 'bg-surface-3 text-text-muted hover:bg-surface-strong'
              }`}
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Stat label="Outstanding" value={formatCurrency(totals.open_cents)} tone="warning" />
        <Stat label="Paid (30d)" value={formatCurrency(totals.paid_30d_cents)} tone="success" />
        <Stat label="Uncollectible" value={formatCurrency(totals.uncollectible_cents)} tone={totals.uncollectible_cents > 0 ? 'danger' : 'neutral'} />
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-sm text-text-muted">
          Loading invoices…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" /></svg>}
          title={invoices && invoices.length > 0 ? 'No invoices in this filter' : 'No invoices yet'}
          subtitle={invoices && invoices.length > 0 ? 'Try another status.' : 'Invoices are issued from Zoho Billing; the sync into PazPortal is not built yet.'}
        />
      ) : (
        <>
          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {filtered.map((i) => (
              <button
                key={i.id}
                onClick={() => i.customer?.id && navigate(`/customers/${i.customer.id}`)}
                className="w-full text-left rounded-lg border border-border bg-surface p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-text">{i.number ?? 'Invoice'}</p>
                    <p className="text-xs text-text-muted">{i.customer?.name ?? '—'}</p>
                  </div>
                  <StatusBadge status={i.status} />
                </div>
                <p className="text-xs text-text-faint mt-1.5">
                  {formatCurrency(i.amount_due_cents)} · due {i.due_at ? formatDate(i.due_at) : '—'}
                </p>
              </button>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-surface-3 text-left">
                <tr>
                  <th className="px-3 py-2.5 font-medium">Number</th>
                  <th className="px-3 py-2.5 font-medium">Customer</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-3 py-2.5 font-medium text-right">Amount</th>
                  <th className="px-3 py-2.5 font-medium">Issued</th>
                  <th className="px-3 py-2.5 font-medium">Due</th>
                  <th className="px-3 py-2.5 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {filtered.map((i) => (
                  <tr
                    key={i.id}
                    className="bg-surface hover:bg-surface-strong cursor-pointer"
                    onClick={() => i.customer?.id && navigate(`/customers/${i.customer.id}`)}
                  >
                    <td className="px-3 py-2.5">{i.number ?? '—'}</td>
                    <td className="px-3 py-2.5">{i.customer?.name ?? '—'}</td>
                    <td className="px-3 py-2.5">
                      <StatusBadge status={i.status} />
                    </td>
                    <td className="px-3 py-2.5 text-right">{formatCurrency(i.amount_due_cents)}</td>
                    <td className="px-3 py-2.5 text-xs text-text-muted">{i.issued_at ? formatDate(i.issued_at) : '—'}</td>
                    <td className="px-3 py-2.5 text-xs text-text-muted">{i.due_at ? formatDate(i.due_at) : '—'}</td>
                    <td className="px-3 py-2.5">
                      {i.hosted_invoice_url && (
                        <a
                          href={i.hosted_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-primary hover:underline"
                        >
                          View invoice
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
}) {
  const colour = {
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-error',
    neutral: 'text-text',
  }[tone];
  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <p className="text-xs text-text-muted">{label}</p>
      <p className={`text-2xl font-semibold mt-1 ${colour}`}>{value}</p>
    </div>
  );
}
